"use client";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/pop-ui/pop-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSafeKeyboardFix } from "@/hooks/use-safe-keyboard-fix";
import { authClient } from "@/lib/auth/auth-client";
import { getCurrentAreaInClient, getCurrentLangInClient } from "@/lib/utils.client";
import type { CheckoutAddressFormData } from "@/modules/checkout/types";
import { useORPC } from "@/providers/orpc-provider";
import { AddressConfirmationDialog } from "./address-confirmation-dialog";
import AddressFinder from "./address-finder";
import { AddressModificationDialog } from "./address-modification-dialog";
import { getPhoneConfig, getProvinceOptions } from "./config/address-config";
import type { AddressValidationResult } from "./config/address-validation-service";
import { AddressValidationService } from "./config/address-validation-service";
interface AddAddressFormDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onAddAddress: (newAddressData: CheckoutAddressFormData) => void;
  onUpdateAddress: (updatedAddressData: CheckoutAddressFormData & {
    id: string;
  }) => void;
  onDeleteAddress?: (addressId: string) => void;
  editingAddress: CheckoutAddressFormData | null;
  showDelete?: boolean;
  dismissible?: boolean;
}
interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  addressDetail?: string;
  city?: string;
  province?: string;
  zipCode?: string;
}
const dinProBold = "font-['DINPro-Bold']";
const dinProRegular = "font-['DINPro-Regular']";
export const AddAddressFormDialog: React.FC<AddAddressFormDialogProps> = ({
  isOpen,
  onOpenChange,
  onAddAddress,
  onUpdateAddress,
  onDeleteAddress,
  editingAddress,
  showDelete = false,
  dismissible = true
}) => {
  const {
    t
  } = useTranslation();
  const isMobile = useIsMobile();
  const {
    data: session
  } = authClient.useSession();
  const orpc = useORPC();

  // 获取当前地区信息
  const currentArea = getCurrentAreaInClient();
  const {
    data: areaData
  } = useQuery({
    ...orpc.area.findByCode.queryOptions(),
    enabled: !!currentArea,
    staleTime: 1000 * 60 * 60,
    // 1小时缓存
    queryKey: ["area", "findByCode", currentArea]
  });

  // Handle keyboard viewport issues on mobile
  const fixViewport = useSafeKeyboardFix();

  // 生成唯一ID
  const firstNameId = useId();
  const lastNameId = useId();
  const phoneId = useId();
  const addressId = useId();
  const addressDetailId = useId();
  const cityId = useId();
  const provinceId = useId();
  const zipCodeId = useId();
  const isDefaultId = useId();

  // Handler for drawer close with viewport fix
  const handleDrawerOpenChange = (open: boolean) => {
    onOpenChange(open);

    // Fix viewport when closing on mobile
    if (!open && isMobile) {
      fixViewport();
    }
  };
  const userIsAnonymous = session?.user ? Boolean(session.user.isAnonymous) : true;
  const initialFormData = useMemo(() => {
    return {
      firstName: "",
      lastName: "",
      phone: "",
      address: "",
      addressDetail: "",
      province: "",
      city: "",
      zipCode: "",
      isDefault: false
    };
  }, []);
  const [formData, setFormData] = useState<CheckoutAddressFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 新增：是否展开手动/详细表单
  const [isExpanded, setIsExpanded] = useState(false);
  // 地址 Finder 的受控值，用于"≥3字符后启用联想"
  const [addressFinderValue, setAddressFinderValue] = useState("");

  // 新增：地址校验相关状态
  const [showAddressConfirmation, setShowAddressConfirmation] = useState(false);
  const [showAddressModification, setShowAddressModification] = useState(false);
  const [addressValidationResult, setAddressValidationResult] = useState<AddressValidationResult | null>(null);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);

  // 获取当前地区的手机号前缀（来自接口）
  const currentPhonePrefix = areaData?.phonePrefix || null;
  const phonePrefixDisplay = useMemo(() => {
    if (!currentPhonePrefix) return "+";
    return currentPhonePrefix.startsWith("+") ? currentPhonePrefix : `+${currentPhonePrefix}`;
  }, [currentPhonePrefix]);

  // 手机号输入值（不包含前缀）
  const phoneInputValue = useMemo(() => {
    if (!formData.phone) return "";
    // 直接返回手机号，移除所有非数字字符
    return formData.phone.replace(/\D/g, "");
  }, [formData.phone]);

  // 获取当前地区名称（来自接口）
  const currentAreaName = useMemo(() => {
    const lang = getCurrentLangInClient();
    const nameTrans = areaData?.name_trans;
    if (!nameTrans) return currentArea;

    // 根据语言返回对应的名称
    return nameTrans[lang];
  }, [areaData?.name_trans, currentArea]);

  // 获取省份选项 - 统一使用英文，简化语言处理
  const provinceOptions = useMemo(() => {
    const options = getProvinceOptions(currentArea);
    return options;
  }, [currentArea]);

  // 初始化地区代码和自动完成状态
  useEffect(() => {
    if (!isOpen) {
      setValidationErrors({});
      setIsExpanded(false);
    }
  }, [isOpen]);

  // 验证函数（通用规则）
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case "firstName":
        if (!value.trim()) return t("validation.firstName.required");
        if (value.length < 2 || value.length > 30) return t("validation.firstName.length");
        if (!/^[a-zA-Z\u4e00-\u9fa5\s'-]+$/.test(value)) return t("validation.firstName.format");
        break;
      case "lastName":
        if (!value.trim()) return t("validation.lastName.required");
        if (value.length < 2 || value.length > 30) return t("validation.lastName.length");
        if (!/^[a-zA-Z\u4e00-\u9fa5\s'-]+$/.test(value)) return t("validation.lastName.format");
        break;
      case "phone":
        {
          if (!value.trim()) return t("validation.phone.required");
          // 移除所有非数字字符，仅校验数字长度
          const digits = value.replace(/\D/g, "").trim();

          // 使用配置文件中的手机号模式进行校验
          const phoneConfig = getPhoneConfig(currentArea);
          if (phoneConfig?.pattern) {
            if (!phoneConfig.pattern.test(digits)) {
              // 如果有地区特定的错误文案，使用它；否则使用通用文案
              return t(`validation.phone.format.${currentArea.toLowerCase()}`) || t("validation.phone.format");
            }
          } else {
            // 如果没有配置，使用默认校验逻辑
            if (!/^[0-9]{8,15}$/.test(digits)) {
              return t("validation.phone.format");
            }
          }
          break;
        }
      case "address":
        {
          if (!value.trim()) return t("validation.address.required");
          if (value.length < 5 || value.length > 120) return t("validation.address.length");
          break;
        }
      case "addressDetail":
        {
          if (value && (value.length < 2 || value.length > 120)) return t("validation.addressDetail.length");
          break;
        }
      case "city":
        if (!value.trim()) return t("validation.city.required");
        break;
      case "province":
        if (!value.trim()) return t("validation.province.required");
        break;
      case "zipCode":
        if (!value.trim()) return t("validation.zipCode.required");
        break;
    }
    return undefined;
  };

  // 验证所有字段
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== "isDefault") {
        const error = validateField(key, formData[key as keyof typeof formData] as string);
        if (error) {
          errors[key as keyof ValidationErrors] = error;
        }
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    const getComponentFromPlace = (componentType: string, nameType: "long_name" | "short_name" = "long_name"): string => {
      const component = place.address_components?.find(comp => comp.types.includes(componentType));
      return component?.[nameType] || "";
    };
    if (place?.address_components) {
      const subLocality = getComponentFromPlace("sublocality_level_1");
      // 优先获取 postal_town，如果没有则尝试 locality
      const city = getComponentFromPlace("postal_town") || getComponentFromPlace("locality");
      const provinceName = getComponentFromPlace("administrative_area_level_1");
      const postalCode = getComponentFromPlace("postal_code");
      // const country = getComponentFromPlace('country')
      // const countryCode = getComponentFromPlace('country', 'short_name') // Get country code (e.g., "GB")

      const mainAddress = place.formatted_address || "";
      let detailAddress = "";
      if (subLocality) detailAddress = subLocality;
      let matchedProvince = provinceName;
      if (provinceName && provinceOptions.length > 0) {
        const directMatch = provinceOptions.find(option => option.toLowerCase() === provinceName.toLowerCase());
        if (directMatch) {
          matchedProvince = directMatch;
        }
      }
      setFormData(prev => ({
        ...prev,
        address: mainAddress,
        addressDetail: detailAddress,
        city: city || prev.city,
        province: matchedProvince || prev.province,
        zipCode: postalCode || prev.zipCode
      }));

      // 清除相关字段的验证错误
      setValidationErrors(prev_0 => ({
        ...prev_0,
        address: undefined,
        city: city ? undefined : prev_0.city,
        province: provinceName ? undefined : prev_0.province,
        zipCode: postalCode ? undefined : prev_0.zipCode
      }));

      // 选择后展开剩余输入项，并把 Finder 变为普通输入框
      setIsExpanded(true);
    } else if (place && (place.name || place.formatted_address)) {
      setFormData(prev_1 => ({
        ...prev_1,
        address: place.formatted_address || place.name || prev_1.address,
        addressDetail: "",
        city: "",
        province: "",
        zipCode: ""
      }));
      setValidationErrors(prev_2 => ({
        ...prev_2,
        address: undefined
      }));
      setIsExpanded(true);
    }
  };

  // 处理表单数据初始化和回显
  useEffect(() => {
    if (isOpen) {
      if (editingAddress) {
        // 编辑地址时，清理手机号，只保留数字
        let phoneNumber = editingAddress.phone;
        if (phoneNumber) {
          // 移除所有非数字字符
          phoneNumber = phoneNumber.replace(/\D/g, "");
        }
        setFormData({
          ...editingAddress,
          phone: phoneNumber
        });
        setIsExpanded(true); // 编辑时直接展开
      } else {
        // 新增地址：使用初始数据
        setFormData({
          ...initialFormData
        });
        setIsExpanded(false);
      }
    }
  }, [isOpen, editingAddress, initialFormData]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name: name_0,
      value: value_0,
      type,
      checked
    } = e.target;
    let newValue = value_0;

    // 处理手机号输入 - 只保留数字
    if (name_0 === "phone") {
      newValue = value_0.replace(/\D/g, "");
    }
    setFormData(prev_3 => ({
      ...prev_3,
      [name_0]: type === "checkbox" ? checked : newValue
    }));

    // 实时验证
    if (type !== "checkbox") {
      const error_0 = validateField(name_0, newValue);
      setValidationErrors(prev_4 => ({
        ...prev_4,
        [name_0]: error_0
      }));
    }
  };
  const handleSelectChange = (fieldName: string, value_1: string | boolean) => {
    // 如果是默认地址，不允许取消勾选
    if (fieldName === "isDefault" && editingAddress?.isDefault && Boolean(editingAddress.isDefault)) {
      toast.error(t("order.mustHaveOneDefaultAddress"));
      return;
    }
    setFormData(prev_5 => ({
      ...prev_5,
      [fieldName]: value_1
    }));
    if (typeof value_1 === "string") {
      const error_1 = validateField(fieldName, value_1);
      setValidationErrors(prev_6 => ({
        ...prev_6,
        [fieldName]: error_1
      }));
    }
  };
  const handleSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    if (!validateForm()) {
      // 如果验证失败且当前不是手动模式，自动切换到手动模式以便显示验证错误
      if (!isExpanded) {
        // 将 AddressFinder 中当前输入的值同步到 formData.address
        if (addressFinderValue.trim()) {
          setFormData(prev_7 => ({
            ...prev_7,
            address: addressFinderValue.trim()
          }));
        }
        setIsExpanded(true);
      }
      return;
    }

    // 新增：地址校验逻辑
    setIsValidatingAddress(true);
    try {
      // 获取当前地区代码
      const validationResult = await AddressValidationService.validateAddress(formData, currentArea);
      setAddressValidationResult(validationResult);

      // 根据验证结果决定下一步操作

      // 优先级顺序：推荐弹窗 > 修改弹窗 > 直接提交
      if (validationResult.shouldShowSuggestionDialog && validationResult.suggestedAddress) {
        // 有改进建议，优先弹推荐弹窗
        setShowAddressConfirmation(true);
      } else if (validationResult.issueKeys && validationResult.issueKeys.length > 0) {
        // 有验证问题，弹修改弹窗
        setShowAddressModification(true);
      } else if (validationResult.isValid) {
        // 地址完全通过，直接提交（优先级最低）
        await submitAddress(formData);
      } else {
        // 兜底：如果既没有推荐也没有问题，但 isValid 为 false，也弹修改弹窗
        setShowAddressModification(true);
      }
    } catch (error_2) {
      console.error("Address validation failed:", error_2);
      // 如果地址校验失败，显示错误提示，不提交地址
      toast.error(t("order.addressValidationFailed"));
    } finally {
      setIsValidatingAddress(false);
    }
  };

  // 新增：提交地址的函数
  const submitAddress = async (addressData: CheckoutAddressFormData) => {
    setIsSubmitting(true);
    const finalData = {
      ...addressData,
      isDefault: Boolean(addressData.isDefault)
    };
    try {
      if (editingAddress?.id) {
        await onUpdateAddress({
          ...finalData,
          id: editingAddress.id
        });
      } else {
        await onAddAddress(finalData);
      }
      onOpenChange(false);
    } catch (error_3) {
      // 如果请求失败，保持弹窗打开，用户可以重试
      console.error("Address operation failed:", error_3);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 新增：处理地址确认的回调
  const handleAddressConfirm = async (selectedAddress: CheckoutAddressFormData) => {
    // 使用用户在弹窗中选择的地址（可能是原始地址或建议地址）
    await submitAddress(selectedAddress);
    setShowAddressConfirmation(false);
  };

  // 新增：处理返回编辑的回调
  const handleBackToEdit = () => {
    setShowAddressConfirmation(false);
  };

  // 新增：处理修改弹窗返回编辑的回调
  const handleModificationBackToEdit = () => {
    setShowAddressModification(false);
  };

  // 新增：处理修改弹窗使用输入地址的回调
  const handleUseEnteredAddress = async () => {
    // 使用用户输入的地址
    await submitAddress(formData);
    setShowAddressModification(false);
  };
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };
  const handleConfirmDelete = async () => {
    if (editingAddress?.id && onDeleteAddress) {
      setIsDeleting(true);
      try {
        onDeleteAddress(editingAddress.id);
        setShowDeleteConfirm(false);
        onOpenChange(false);
      } catch (error_4) {
        console.error("Delete address failed:", error_4);
        toast.error(t("common.error.deleteFailed"));
      } finally {
        setIsDeleting(false);
      }
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // 共用的表单内容
  const formContent = <form onSubmit={handleSubmit} noValidate className={`${isMobile ? "flex h-full min-h-0 flex-col" : "flex h-full flex-grow flex-col overflow-hidden"}`} style={isMobile ? {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column"
  } : {}}>
			{/* 配送至标识 */}
			<div className="flex flex-shrink-0 items-center gap-2 px-4 pt-4 pb-4 md:px-12 md:pt-0">
				<div className={`flex items-center text-sm text-black`}>
					<span className={dinProRegular}>{t("order.deliverTo")}</span>
					<span className={`${dinProBold} ml-1 font-bold`}>
						{currentAreaName}
					</span>
				</div>
			</div>

			<div className={`space-y-[15px] px-4 pb-4 md:px-12 ${isMobile ? "min-h-0 flex-1 overflow-y-auto" : "flex-grow overflow-visible"}`} style={isMobile ? {
      WebkitOverflowScrolling: "touch",
      touchAction: "pan-y",
      overscrollBehavior: "contain"
    } : {}}>
				{/* 姓名 */}
				<div className={`${isMobile ? "space-y-[15px]" : "grid grid-cols-2 gap-3"}`}>
					<div>
						<Input id={firstNameId} name="firstName" placeholder={t("order.firstNamePlaceholder")} value={formData.firstName} onChange={handleChange} required autoComplete="off" className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9] ${validationErrors.firstName ? "border-red-500" : ""}`} />
						{validationErrors.firstName && <p className="mt-1 font-['DINPro-Regular'] text-xs text-[#D2001E]">
								{validationErrors.firstName}
							</p>}
					</div>
					<div>
						<Input id={lastNameId} name="lastName" placeholder={t("order.lastNamePlaceholder")} value={formData.lastName} onChange={handleChange} required autoComplete="off" className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9] ${validationErrors.lastName ? "border-red-500" : ""}`} />
						{validationErrors.lastName && <p className="mt-1 font-['DINPro-Regular'] text-xs text-[#D2001E]">
								{validationErrors.lastName}
							</p>}
					</div>
				</div>

				{/* 电话 */}
				<div>
					<div className={`border-input hover:border-ring focus-within:border-ring focus-within:ring-ring/15 flex h-12 border transition-[color,box-shadow] focus-within:ring-[2px] bg-[#F9F9F9] ${validationErrors.phone ? "border-destructive focus-within:border-destructive focus-within:ring-destructive/20" : ""}`}>
						<div className="flex items-center px-3 font-['DINPro-Regular'] text-sm text-black">
							{phonePrefixDisplay}
						</div>
						<div className="flex items-center">
							<div className="h-4 w-px bg-gray-300"></div>
						</div>
						<input id={phoneId} name="phone" type="tel" inputMode="numeric" pattern="[0-9]*" placeholder={t("order.phonePlaceholder")} value={phoneInputValue} onChange={handleChange} required autoComplete="off" className={`${dinProRegular} placeholder:text-muted-foreground flex-1 border-none bg-transparent px-3 py-1 text-sm text-black outline-none`} />
					</div>
					{validationErrors.phone && <p className="mt-1 font-['DINPro-Regular'] text-xs text-[#D2001E]">
							{validationErrors.phone}
						</p>}
				</div>

				{/* 分割线 */}
				<div className="my-5 h-px w-full bg-[#eee]"></div>

				{/* Address Finder：作为快捷方式，展开后隐藏，不计入表单 */}
				{!isExpanded && <AddressFinder currentAreaCode={currentArea} className="" onSelect={place_0 => {
        handlePlaceSelected(place_0);
      }} onManual={val => {
        setFormData(prev_8 => ({
          ...prev_8,
          address: val
        }));
        setIsExpanded(true);
      }} onChange={setAddressFinderValue} />}

				{/* 展开后的地址字段：原 Finder 变成普通地址输入框 */}
				{isExpanded && <>
						<div>
							<Input id={addressId} name="address" placeholder={t("order.addressPlaceholder")} value={formData.address} onChange={handleChange} required autoComplete="off" className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9] ${validationErrors.address ? "border-red-500" : ""}`} />
							{validationErrors.address && <p className="mt-1 font-['DINPro-Regular'] text-xs text-[#D2001E]">
									{validationErrors.address}
								</p>}
						</div>

						<div>
							<Input id={addressDetailId} name="addressDetail" placeholder={t("order.addressDetailPlaceholder")} value={formData.addressDetail} onChange={handleChange} autoComplete="off" className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9] ${validationErrors.addressDetail ? "border-red-500" : ""}`} />
							{validationErrors.addressDetail && <p className="mt-1 font-['DINPro-Regular'] text-xs text-[#D2001E]">
									{validationErrors.addressDetail}
								</p>}
						</div>

						<div>
							<Input id={cityId} name="city" placeholder={t("order.cityPlaceholder")} value={formData.city} onChange={handleChange} required autoComplete="off" className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9] ${validationErrors.city ? "border-red-500" : ""}`} />
							{validationErrors.city && <p className="mt-1 font-['DINPro-Regular'] text-xs text-[#D2001E]">
									{validationErrors.city}
								</p>}
						</div>

						<div className={`${isMobile ? "space-y-[15px]" : "grid grid-cols-2 gap-3"}`}>
							<div>
								<Select name="province" value={formData.province} onValueChange={value_2 => handleSelectChange("province", value_2)}>
									<SelectTrigger id={provinceId} className={`${dinProRegular}  w-full text-black shadow-none bg-[#F9F9F9] ${validationErrors.province ? "border-red-500" : ""}`}>
										<SelectValue placeholder={t("order.provincePlaceholder")} />
									</SelectTrigger>
									<SelectContent>
										{provinceOptions.map(option_0 => <SelectItem key={option_0} value={option_0}>
												{option_0}
											</SelectItem>)}
									</SelectContent>
								</Select>
								{validationErrors.province && <p className="mt-1 font-['DINPro-Regular'] text-xs text-[#D2001E]">
										{validationErrors.province}
									</p>}
							</div>
							<div>
								<Input id={zipCodeId} name="zipCode" placeholder={t("order.zipCodePlaceholder")} value={formData.zipCode} onChange={handleChange} required autoComplete="off" className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9] ${validationErrors.zipCode ? "border-red-500" : ""}`} />
								{validationErrors.zipCode && <p className="mt-1 font-['DINPro-Regular'] text-xs text-[#D2001E]">
										{validationErrors.zipCode}
									</p>}
							</div>
						</div>

						{/* 回到联想模式 */}
						<div className="mt-2 text-right">
							<button type="button" className={`${dinProRegular} text-xs text-gray-700 underline`} onClick={() => {
            setIsExpanded(false);
          }}>
								{t("order.findAnotherAddress")}
							</button>
						</div>
					</>}

				{!userIsAnonymous && <div className="flex items-center space-x-2 py-2">
						<Checkbox id={isDefaultId} name="isDefault" checked={Boolean(formData.isDefault)} onCheckedChange={checked_0 => handleSelectChange("isDefault", Boolean(checked_0))} className={`rounded-none border-black bg-[#F9F9F9] data-[state=checked]:bg-black data-[state=checked]:text-white ${editingAddress?.isDefault && Boolean(editingAddress.isDefault) ? "cursor-not-allowed opacity-50" : ""}`} />
						<label htmlFor={isDefaultId} className={`text-sm ${dinProRegular} ${editingAddress?.isDefault && Boolean(editingAddress.isDefault) ? "cursor-not-allowed text-gray-600" : "text-black"}`}>
							{t("order.setAsDefaultAddressLabel")}
						</label>
					</div>}
			</div>

			{/* 按钮区域 - 移动端固定在底部，桌面端也固定 */}
			<div className={`flex-shrink-0 px-4 pt-4 md:px-12 ${isMobile ? "border-t bg-white pb-6" : "pb-4 md:pb-8"}`} style={isMobile ? {
      paddingBottom: "max(24px, calc(24px + env(safe-area-inset-bottom)))",
      position: "sticky",
      bottom: 0,
      zIndex: 10
    } : {}}>
				<Button type="submit" loading={isSubmitting || isValidatingAddress} className={`${dinProBold} h-12 w-full rounded-none bg-black text-sm font-bold text-white hover:bg-black`}>
					{isValidatingAddress ? t("order.validatingAddress") : t("order.saveAddressButton")}
				</Button>
				{!userIsAnonymous && showDelete && editingAddress && editingAddress.id && onDeleteAddress && <Button type="button" variant="outline" onClick={handleDeleteClick} className={`${dinProBold} mt-3 h-12 w-full rounded-none text-sm font-bold`}>
							{t("order.deleteAddressButton")}
						</Button>}
			</div>
		</form>;

  // 移动端使用 Drawer
  if (isMobile) {
    return <>
				<Drawer open={isOpen} onOpenChange={handleDrawerOpenChange} direction="right" dismissible={dismissible} shouldScaleBackground={false}>
					<DrawerContent className={`fixed inset-0 h-full !w-full !max-w-none ${dinProRegular} flex flex-col overflow-hidden`} style={{
          width: "100%",
          maxWidth: "none",
          minHeight: "100dvh",
          height: "100dvh"
        }}>
						<DrawerHeader className="w-full flex-shrink-0 border-b bg-white p-4 text-left">
							<div className="flex items-center justify-between">
								<DrawerTitle className={`text-lg ${dinProBold} text-gray-900`}>
									{editingAddress ? t("order.editAddress") : t("order.addNewAddress")}
								</DrawerTitle>
								<Button variant="ghost" size="sm" onClick={() => handleDrawerOpenChange(false)} className="h-8 w-8 p-0 hover:bg-gray-100">
									<XMarkIcon className="h-5 w-5 text-gray-500" />
								</Button>
							</div>
						</DrawerHeader>
						<div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white" style={{
            touchAction: "manipulation"
          }}>
							{formContent}
						</div>
					</DrawerContent>
				</Drawer>

				{/* 删除确认弹窗 - 移动端也使用 Drawer */}
				<Drawer open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm} direction="bottom" shouldScaleBackground={false}>
					<DrawerContent className="h-auto">
						<DrawerHeader className="border-b bg-white p-4 text-left">
							<div className="flex items-center justify-between">
								<DrawerTitle className={`text-lg ${dinProBold} text-gray-900`}>
									{t("order.deleteAddressConfirmTitle")}
								</DrawerTitle>
								<Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)} className="h-8 w-8 p-0 hover:bg-gray-100">
									<XMarkIcon className="h-5 w-5 text-gray-500" />
								</Button>
							</div>
						</DrawerHeader>
						<div className="p-8 text-center">
							<p className={`${dinProRegular} mb-6 text-sm leading-relaxed text-gray-600`}>
								{t("order.deleteAddressConfirmDescription")}
							</p>
							<div className="space-y-3">
								<Button onClick={handleConfirmDelete} loading={isDeleting} className={`${dinProBold} h-12 w-full rounded-none bg-black text-sm font-bold text-white hover:bg-gray-800`}>
									{t("order.confirmDelete")}
								</Button>
								<Button onClick={handleCancelDelete} variant="outline" className={`${dinProBold} h-12 w-full rounded-none border-gray-300 text-sm font-bold text-black hover:bg-gray-50`}>
									{t("common.button.cancel")}
								</Button>
							</div>
						</div>
					</DrawerContent>
				</Drawer>

				{/* 地址确认弹窗 */}
				{showAddressConfirmation && addressValidationResult && addressValidationResult.suggestedAddress && <AddressConfirmationDialog isOpen={showAddressConfirmation} onOpenChange={setShowAddressConfirmation} originalAddress={formData} suggestedAddress={addressValidationResult.suggestedAddress} onConfirm={handleAddressConfirm} onBackToEdit={handleBackToEdit} />}

				{/* 地址修改弹窗 */}
				{showAddressModification && addressValidationResult && <AddressModificationDialog isOpen={showAddressModification} onOpenChange={setShowAddressModification} addressData={formData} validationResult={addressValidationResult} onConfirm={handleUseEnteredAddress} onBackToEdit={handleModificationBackToEdit} />}
			</>;
  }

  // 桌面端使用 Dialog
  return <>
			<Dialog open={isOpen} onOpenChange={onOpenChange} modal={true}>
				<DialogContent className={`max-h-[90vh] !w-[600px] !max-w-[600px] ${dinProRegular} flex flex-col overflow-hidden rounded-none bg-white p-0`} onPointerDownOutside={e_1 => {
        const target = e_1.target as Element;
        if (target?.closest(".pac-container") || target?.closest(".pac-item") || target?.classList.contains("pac-item") || target?.classList.contains("pac-container") || target?.closest('[class*="pac-"]')) {
          e_1.preventDefault();
          return;
        }
        e_1.preventDefault();
      }} onEscapeKeyDown={e_2 => e_2.preventDefault()} onOpenAutoFocus={e_3 => e_3.preventDefault()}>
					<div style={{
          display: "none"
        }}>
						<DialogTitle>{t("order.addressDialogTitle")}</DialogTitle>
					</div>
					<div className={`pt-[32px] pb-2 text-center ${dinProBold} flex-shrink-0 text-base leading-4 font-bold text-black`}>
						{editingAddress ? t("order.editAddress") : t("order.addNewAddress")}
					</div>
					{formContent}
				</DialogContent>
			</Dialog>

			{/* 删除确认弹窗 - 桌面端使用 Dialog */}
			<Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
				<DialogContent className="w-[400px] rounded-none bg-white p-0">
					<div style={{
          display: "none"
        }}>
						<DialogTitle>{t("order.deleteAddressConfirmTitle")}</DialogTitle>
					</div>
					<div className="p-8 text-center">
						<h3 className={`${dinProBold} mb-4 text-lg font-bold text-black`}>
							{t("order.deleteAddressConfirmTitle")}
						</h3>
						<p className={`${dinProRegular} mb-6 text-sm leading-relaxed text-gray-600`}>
							{t("order.deleteAddressConfirmDescription")}
						</p>
						<div className="space-y-3">
							<Button onClick={handleConfirmDelete} loading={isDeleting} className={`${dinProBold} h-12 w-full rounded-none bg-black text-sm font-bold text-white hover:bg-gray-800`}>
								{t("order.confirmDelete")}
							</Button>
							<Button onClick={handleCancelDelete} variant="outline" className={`${dinProBold} h-12 w-full rounded-none border-gray-300 text-sm font-bold text-black hover:bg-gray-50`}>
								{t("common.button.cancel")}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* 地址确认弹窗 */}
			{showAddressConfirmation && addressValidationResult && addressValidationResult.suggestedAddress && <AddressConfirmationDialog isOpen={showAddressConfirmation} onOpenChange={setShowAddressConfirmation} originalAddress={formData} suggestedAddress={addressValidationResult.suggestedAddress} onConfirm={handleAddressConfirm} onBackToEdit={handleBackToEdit} />}

			{/* 地址修改弹窗 */}
			{showAddressModification && addressValidationResult && <AddressModificationDialog isOpen={showAddressModification} onOpenChange={setShowAddressModification} addressData={formData} validationResult={addressValidationResult} onConfirm={handleUseEnteredAddress} onBackToEdit={handleModificationBackToEdit} />}
		</>;
};